import { useQuery } from '@apollo/client';
import {
  B1,
  HeaderTag,
  getTranslatedText,
  Loader,
  OPERATORMAP,
  EventEmitterWithIndex
} from '@nile/components';
import { Avatar, DisplayText, TextStyle } from '@superops/ui-design-system';
import {
  AccountClient,
  GET_ENTITY_PROPERTIES,
  useCachedQuery
} from '@nile/graphql';
import { GET_AUTOMATION_RULE } from '@nile/banyan/src/Graphql/Mutations/automationrules';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import nanoid from 'nanoid';
import { injectIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';

const CombinatorTag = styled.div`
  .combinator {
    background: ${(props) => (props.value === 'OR' ? '#E65D0F' : '#6C69FF')};
  }
  .triangle-right {
    border-left: 10px solid
      ${(props) => (props.value === 'OR' ? '#E65D0F' : '#6C69FF')};
  }
`;

function TimeTriggerViewMode(props) {
  const { timeTrigger, intl } = props;
  // const { ruleCondition, event, timerDuration } = timeTrigger;
  const { loading, data, error } = useQuery(GET_ENTITY_PROPERTIES, {
    client: AccountClient
  });
  const {
    loading: ruleLoading,
    error: ruleError,
    data: automationRule,
    refetch
  } = useCachedQuery(GET_AUTOMATION_RULE, {
    variables: { id: timeTrigger.id, automationRuleType: 'TIME_TRIGGERS' },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    EventEmitterWithIndex.subscribe('refreshTimeTrigger', () => {
      refetch();
    });
    return () => {
      EventEmitterWithIndex.unsubscribe('refreshTimeTrigger');
    };
  }, []);

  const { ruleCondition, event, timerDuration } =
    automationRule?.getAutomationRule || {};
  const addIds = (ruleGroup, type = 'group') => {
    const ruleGroupCopy = { ...ruleGroup };
    ruleGroupCopy.id = type === 'group' ? `g-${nanoid()}` : `r-${nanoid()}`;
    if (ruleGroupCopy.operands) {
      ruleGroupCopy.operands = ruleGroupCopy.operands.map((rule) =>
        addIds(rule, 'rule')
      );
    }
    return ruleGroupCopy;
  };
  const ruleConditions = ruleCondition;
  const rule = addIds(ruleConditions);

  const isRuleGroup = (ruleOrGroup) => {
    return !!(ruleOrGroup?.joinOperator && ruleOrGroup?.operands);
  };
  const getLevel = (i, index = 0, query = rule) => {
    let foundAtIndex = -1;
    if (query?.id === i) {
      foundAtIndex = index;
    } else if (isRuleGroup(query)) {
      query.operands.forEach((r) => {
        if (foundAtIndex === -1) {
          let indexForRule = index;
          if (isRuleGroup(r)) indexForRule++;
          foundAtIndex = getLevel(i, indexForRule, r);
        }
      });
    }
    return foundAtIndex;
  };

  const eventMap = {
    TICKET_CREATED: getTranslatedText(intl, 'admin.timeTriggers.ticketCreated'),
    TICKET_UPDATED: getTranslatedText(intl, 'admin.timeTriggers.ticketUpdated')
  };

  const durMap = {
    DAY: getTranslatedText(intl, 'admin.timeTriggers.duration.days'),
    HOUR: getTranslatedText(intl, 'admin.timeTriggers.duration.hours'),
    MINUTE: getTranslatedText(intl, 'admin.timeTriggers.duration.minutes')
  };

  const timeOrderMap = ['DAY', 'HOUR', 'MINUTE'];

  const formattedTimeDuration = timerDuration
    ? timeOrderMap.map((el) => timerDuration.find((prop) => prop.unit === el))
    : null;

  const getPropertyValue = (property) => {
    if (property?.name === 'site' || property?.name === 'client') {
      return property?.value?.name;
    } else {
      return property?.value;
    }
  };
  if (loading || ruleLoading) return <Loader />;
  const getModifiedAttribute = (attribute) => {
    const attributeParts = attribute.split('.');
    const parentEntity =
      attributeParts.length > 1 ? attributeParts[0] : 'ticket';

    const entityAttributes = data?.getEntitiesMeta?.find(
      ({ name }) =>
        name.toLowerCase() ===
        (parentEntity === 'requester'
          ? 'clientUser'
          : parentEntity
        ).toLowerCase()
    )?.attributes;

    const attributeDetails = entityAttributes?.find(
      ({ name }) =>
        name === (parentEntity === 'ticket' ? attribute : attributeParts[1])
    );

    if (attributeDetails?.isCustom) {
      return attributeDetails.label;
    }

    return attribute.indexOf('.') > -1
      ? attribute.split('.').join(' ')
      : attribute;
  };
  const renderActionType = (action) => {
    switch (action.type) {
      case 'PROPERTY_UPDATE':
        return action.data.map((property) => {
          return (
            <div
              className="flex center mt-10 border br-5 height50"
              title="Assign to"
            >
              <div className="full-height flex center px-10p w150p rounded-5px-left-tb border-right duration-bg-color">
                <div className="mr-10 flex">
                  <SVGIcon name="TicketOutline" />
                </div>
                <FormattedMessage id="admin.eventTrigger.actions.setProperty" />
              </div>
              <div className="fx actr">
                <div className="attribute capitalize ml-10 bold">
                  {getModifiedAttribute(property.name)}
                </div>
                <div className="operator ml-10">
                  {' '}
                  <FormattedMessage id="admin.eventTrigger.to" />
                </div>
                <div className="value ml-10 bold">
                  {getPropertyValue(property)}
                </div>
              </div>
            </div>
          );
        });

      case 'ASSIGNMENT':
        return (
          <div
            className="flex center mt-10 border br-5 height50"
            title="Assign to"
          >
            <div className="full-height flex center px-10p w150p rounded-5px-left-tb border-right duration-bg-color">
              <div className="mr-10 flex">
                <SVGIcon name="AssignToMeIcon" />
              </div>
              <FormattedMessage id="admin.slaPolicy.h2.assignto" />
            </div>
            {action.data.map((el) => {
              return (
                <div className="flex g10 center ml-10">
                  <Avatar
                    size="xs"
                    initials={
                      el?.value?.name?.[0] ||
                      el?.value?.firstName?.[0] ||
                      el?.value?.email?.[0]
                    }
                  />
                  <div>
                    {el.value?.name ||
                      `${el.value?.firstName || ''} ${el?.value?.lastName ||
                        el?.value?.lastname ||
                        ''}`}
                  </div>
                </div>
              );
            })}
          </div>
        );
      case 'ENRICHMENT':
        return (
          <div
            className="flex center mt-10 border br-5 height50"
            title="Assign to"
          >
            <div className="full-height flex center px-10p w150p rounded-5px-left-tb border-right duration-bg-color">
              <div className="mr-10 flex">
                <SVGIcon name="OnboardingNotificationTemplate" />
              </div>
              <FormattedMessage id="admin.eventTrigger.createcustom" />
            </div>
            <div className="fx actr">
              <div className="attribute capitalize ml-10 bold">
                {action?.data?.[0]?.value?.name ||
                  action?.data?.[0]?.value?.subject}
              </div>
            </div>
          </div>
        );
      default:
        return '';
    }
  };
  return (
    <div className="scroll ch-70">
      <div className="rounded-4px p12 mb20 border">
        <HeaderTag
          title={getTranslatedText(
            intl,
            'admin.timeTriggers.triggerThisAction.header'
          )}
          isBgStrip
          isNewLayout
        />
        <div className="flex center">
          <SVGIcon name="TimerClock" size="xl" />
          <div className="flex center ml8">
            <div>{eventMap[event]}</div>
            <div className="ml8 flex">
              {formattedTimeDuration
                ? formattedTimeDuration.map((el) => {
                    return (
                      <>
                        {el?.quantity && durMap?.[el?.unit] && (
                          <B1 className="flex">
                            <div className="ml8">{`${el?.quantity} ${
                              durMap?.[el?.unit]
                            }`}</div>
                          </B1>
                        )}
                      </>
                    );
                  })
                : ''}
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-4px p12 mb20 border">
        <HeaderTag
          title={getTranslatedText(
            intl,
            'admin.timeTriggers.conditions.header'
          )}
          isBgStrip
          isNewLayout
        />
        <RuleGroup
          id={rule.id}
          joinOperator={rule.joinOperator}
          operands={rule.operands}
          getLevel={getLevel}
          intl={intl}
          entitiesMeta={data.getEntitiesMeta}
        />
      </div>
      <div className="rounded-4px p12 mb20 border">
        <HeaderTag
          title={getTranslatedText(intl, 'admin.eventTriggers.thenperform')}
          isBgStrip
          isNewLayout
        />
        {automationRule?.getAutomationRule?.action?.map((action) => {
          if (action?.data?.length) {
            return renderActionType(action);
          }
          return '';
        })}
      </div>
    </div>
  );
}

function RuleGroup(props) {
  const { operands, id, getLevel, joinOperator, intl, entitiesMeta } = props;

  const isRuleGroup = (ruleOrGroup) => {
    return !!(ruleOrGroup?.joinOperator && ruleOrGroup?.operands);
  };
  const level = getLevel(id);

  const getModifiedAttribute = (attribute) => {
    const attributeParts = attribute.split('.');
    const parentEntity =
      attributeParts.length > 1 ? attributeParts[0] : 'ticket';

    const entityAttributes = entitiesMeta?.find(
      ({ name }) =>
        name.toLowerCase() ===
        (parentEntity === 'requester'
          ? 'clientUser'
          : parentEntity
        ).toLowerCase()
    )?.attributes;

    const attributeDetails = entityAttributes?.find(
      ({ name }) =>
        name === (parentEntity === 'ticket' ? attribute : attributeParts[1])
    );

    if (attributeDetails?.isCustom) {
      return attributeDetails.label;
    }

    return attribute.indexOf('.') > -1
      ? attribute.split('.').join(' ')
      : attribute;
  };

  return operands.map((r, idx) => (
    <div key={r.id}>
      {idx ? (
        level === 0 ? (
          <CombinatorTag value={joinOperator} className="flex mt8 mb8">
            <div className="plr-8 text-white uppercase combinator">
              <DisplayText size="medium" weight="Bold">
                <TextStyle> {joinOperator}</TextStyle>
              </DisplayText>
            </div>
            <div className="triangle-right"></div>
          </CombinatorTag>
        ) : (
          <CombinatorTag value={joinOperator} className="flex mt8 mb8">
            <div className="plr-8 text-white uppercase combinator">
              <DisplayText size="medium" weight="Bold">
                <TextStyle> {joinOperator}</TextStyle>
              </DisplayText>
            </div>
            <div className="triangle-right"></div>
          </CombinatorTag>
        )
      ) : null}
      {isRuleGroup(r) ? (
        <div className="border p8 rounded-4px">
          <RuleGroup
            id={r.id}
            parentId={id}
            joinOperator={r.joinOperator}
            operands={r.operands}
            getLevel={getLevel}
            intl={intl}
            entitiesMeta={entitiesMeta}
          />
        </div>
      ) : (
        <Rule
          id={r.id}
          parentId={id}
          attribute={getModifiedAttribute(r.attribute)}
          value={r.value}
          operator={r.operator}
          operands={operands}
          index={idx}
          intl={intl}
        />
      )}
    </div>
  ));
}

function Rule(props) {
  const { rule, attribute, operator, value, intl } = props;
  const operatorMap = OPERATORMAP(intl);
  return (
    <div className="fx actr">
      <div className="attribute capitalize ml-10 bold">{attribute}</div>
      <div className="operator ml-10">{operatorMap[operator]}</div>
      <div className="value ml-10 bold">
        {typeof value === 'object' ? value.join(', ') : value}
      </div>
    </div>
  );
}

export default injectIntl(TimeTriggerViewMode);
